/**
 * 사내 연락처 — 독립 페이지 (/contacts)
 *
 * 왜 별도 페이지인가
 *   연락처 자체는 메일(/mail) 안의 한 탭으로도 열 수 있다(사이드바 '메일 › 내/팀/공용 연락처').
 *   하지만 "사내 주소록을 보고 싶다" 는 요구는 메일과 별개의 동작이라, 메일 화면(폴더 사이드바·읽지
 *   않은 수 등)을 통째로 끌고 들어가지 않고 <b>연락처만</b> 보여주는 전용 진입점이 필요하다.
 *   그래서 사이드바 최상위 '사내 연락처' 는 /mail 이 아니라 이 라우트로 온다.
 *
 * 컴포넌트 재사용
 *   화면 본체는 메일과 완전히 같은 {@link ContactsPanel}(개인/팀/공용 3탭)을 그대로 쓴다.
 *   차이는 오직 "어디에 마운트되는가" — 메일 안이 아니라 독립 라우트라는 점뿐이다.
 *
 * 메일 작성 연결
 *   연락처 상세에서 '이 주소로 메일 작성' 을 누르면, /mail 로 튕겨 보내는 대신 이 페이지가 직접
 *   소유한 {@link MailWriteModal} 을 띄운다. MailWriteModal 은 open/onClose/initial/onSent 만으로
 *   자기 데이터(내 정보·서명·폴더)를 알아서 로드하는 자기완결적 컴포넌트라 여기서도 그대로 쓸 수 있다.
 */
import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useLocation } from "react-router-dom";

import ContactsPanel from "../mail/components/contacts/ContactsPanel";
import MailWriteModal  from "../mail/MailWriteModal";

// COMPANY(사내연락처)는 서버의 ContactScope 값이 아니라 화면 탭 식별자다 — ContactsPanel 주석 참고.
// TEAM(팀)은 2026-08-08 폐지 — 목록에서 빠졌으므로 옛 링크(?scope=TEAM)는 아래 기본 탭으로 떨어진다.
// '메일 수신 그룹' 은 여기 없다 — 주소록이 아니라 발송 묶음이라 메일 화면의 독립 뷰(/mail?groups=1)다.
const SCOPES = ["PERSONAL", "COMPANY", "PUBLIC"];

export default function ContactsPage() {
    const location = useLocation();

    // ?scope=PERSONAL 처럼 진입 탭을 딥링크로 지정할 수 있게 둔다(사이드바는 기본 /contacts 로 들어온다).
    // 미지정 시 COMPANY — 이 라우트로 오는 사이드바 항목 이름이 '사내 연락처' 이고 탭 하나가 정확히
    // 그 이름을 달고 있다. 누른 이름과 다른 탭이 열리면 라벨이 거짓말을 하는 셈이다.
    const initialScope = useMemo(() => {
        const s = new URLSearchParams(location.search).get("scope");
        return SCOPES.includes(s) ? s : "COMPANY";
    }, [location.search]);

    const [composeOpen, setComposeOpen]       = useState(false);
    const [composeInitial, setComposeInitial] = useState(null);

    const handleCompose = (initial = null) => {
        setComposeInitial(initial);
        setComposeOpen(true);
    };

    // full-bleed 라우트(App.isFullBleedPage) 라 main 이 height 100vh 를 준다 →
    // 여기서 height 100% 로 받아 ContactsPanel(내부 스크롤 담당)이 꽉 차게 한다.
    return (
        <Box sx={{ height: "100%", bgcolor: "#fafbfc", overflow: "hidden" }}>
            <ContactsPanel onComposeMail={handleCompose} initialScope={initialScope} />

            <MailWriteModal
                open={composeOpen}
                onClose={() => setComposeOpen(false)}
                initial={composeInitial}
                onSent={() => setComposeOpen(false)}
            />
        </Box>
    );
}
